// ── usePolymarketRelayFills ─────────────────────────────────────
//
// Auto-polling React hook backed by /api/polymarket/fills. Returns the
// trader's persisted poly fill history (server-side polled from the
// CLOB by polyFillsPoller and deduped via polymarket_fill_id).
//
// Mirrors usePolymarketRelayOrders' shape so consumers can render an
// "unavailable" state with actionable copy when the relay can't reach
// the org's poly credential, instead of just an empty table.
//
// #498: state is keyed to the authenticated identity the way
// usePolymarketRelayPositions' is — an org switch mutates user.orgId in
// place with this hook still mounted, and the previous account's rows or
// late responses must never surface under the next one. Polling also
// continues through coded unavailable responses at the normal cadence,
// so a credential/org fix recovers without a manual refresh.
//
// #1467: each validated reply also goes to the tab's Polymarket fill-sound
// feed (`polledFillSound`): the first reply of a scope is silent, later new
// rows go through the same gate as Kalshi fills.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listPolyFillsViaRelay,
  type PolyRelayFill,
  type PolyRelayUnavailableCode,
  type PolyReadCredentialContext,
} from '@/lib/polymarket/relay';
import { useAuthStore } from '@/stores/authStore';
import { feedPolledFills, type PolledFill } from '@/stores/polledFillSound';

export type PolyRelayFillsStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface PolyRelayFillsState {
  fills: PolyRelayFill[];
  status: PolyRelayFillsStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  credentialContext: PolyReadCredentialContext;
  lastFetchMs: number | null;
}

export interface UsePolymarketRelayFillsOptions {
  /** Off → hook is inert (no fetch, no poll). Defaults to true. */
  enabled?: boolean;
  /** Poll interval in ms. Defaults to 10000 (fills are less time-sensitive
   *  than open orders, so we don't need the 5s cadence). */
  pollMs?: number;
  /** Cap on fills returned. Forwarded as a query param. */
  limit?: number;
}

export interface UsePolymarketRelayFillsReturn extends PolyRelayFillsState {
  refresh: () => Promise<void>;
}

const IDLE_FILLS_STATE: PolyRelayFillsState = {
  fills: [],
  status: 'idle',
  error: null,
  unavailableCode: null,
  credentialContext: null,
  lastFetchMs: null,
};

/** Hook state plus the account identity it was fetched under. Never returned —
 *  the identity is stripped by the derivation in the hook body. */
interface StampedFillsState extends PolyRelayFillsState {
  identity: string;
}

/** One scope per identity VISIT. The object itself is the epoch token: an
 *  a → b → a round trip arms a fresh object for the second `a`, so a response
 *  from the first visit fails the reference-equality guard even though the
 *  identity string matches again. */
interface FillsScope {
  identity: string;
}

const DEFAULT_POLL_MS = 10_000;

const identityNow = () => {
  const { user } = useAuthStore.getState();
  return `${user?.id ?? ''}:${user?.orgId ?? ''}`;
};

/** A Poly row as the shared fill-sound gate sees it (#1467): the claim key is
 * the relay's immutable ledger row id, the same in every tab (`fill_id` is
 * nullable on legacy rows). */
export function polyFillSound(fill: PolyRelayFill): PolledFill {
  return { fillKey: [fill.ledger_id], orderId: fill.order_id, count: fill.count,
    executedAtMs: Date.parse(fill.executed_at) };
}

export function usePolymarketRelayFills(
  options: UsePolymarketRelayFillsOptions = {},
): UsePolymarketRelayFillsReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  const limit = options.limit;

  // Account identity. Logout unmounts every authenticated surface, but an ORG
  // SWITCH mutates user.orgId in place with this hook still mounted — and the
  // relay resolves fills per (user, org). Selecting the composed string keeps
  // the zustand subscription firing only on a real identity change.
  const identity = useAuthStore((s) => `${s.user?.id ?? ''}:${s.user?.orgId ?? ''}`);

  // State is stamped with the identity it was fetched under and DERIVED back
  // out below, rather than cleared by an effect on identity change. That makes
  // cross-account leakage structurally impossible instead of merely handled:
  // there is no ordering in which the previous account's rows can be returned,
  // and a response still on the wire during an org switch lands stamped with
  // the old identity and is simply never selected. (The effect-clears-state
  // version also tripped the React Compiler lint on both a render-phase ref
  // write and a synchronous setState inside an effect.)
  const [stamped, setStamped] = useState<StampedFillsState>(() => ({
    ...IDLE_FILLS_STATE,
    identity,
  }));
  const current: PolyRelayFillsState =
    stamped.identity === identity ? stamped : IDLE_FILLS_STATE;

  // Synchronous mirror of the committed state. Async result handling reads
  // last-good rows from it instead of a render-time closure, so `refresh`
  // needs no state in its dependency list. Written only from async callbacks,
  // never during render.
  const stateRef = useRef<StampedFillsState>(stamped);

  // Current scope object; re-armed by refresh() whenever the identity moved.
  // Never touched during render — the React Compiler lint forbids that — so
  // the epoch lives entirely in the callback world.
  const scopeRef = useRef<FillsScope | null>(null);
  // Same-scope requests coalesce by holding their scope object here; a new
  // scope's object never matches, so an old scope's in-flight read cannot
  // block the new scope's initial fetch.
  const inFlightRef = useRef<FillsScope | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (scopeRef.current?.identity !== identity) {
      scopeRef.current = { identity };
    }
    const scope = scopeRef.current;
    if (inFlightRef.current === scope) return;
    inFlightRef.current = scope;
    // Anything carried forward must come from THIS identity's state; a stamp
    // from a previous account degrades to the idle baseline instead.
    const carry = (): PolyRelayFillsState =>
      stateRef.current.identity === identity ? stateRef.current : IDLE_FILLS_STATE;
    // The only state-application path. A result that resolves after unmount
    // or under a newer scope is discarded whole — rows, status, and clock.
    const apply = (next: PolyRelayFillsState): boolean => {
      if (!mountedRef.current || scopeRef.current !== scope) return false;
      const resolved: StampedFillsState = { ...next, identity };
      stateRef.current = resolved;
      setStamped(resolved);
      return true;
    };
    if (carry().status === 'idle') apply({ ...carry(), status: 'loading' });
    try {
      const out = await listPolyFillsViaRelay(limit);
      const lastFetchMs = Date.now();
      if (out.ok) {
        const applied = apply({
          fills: out.fills,
          status: 'ready',
          error: null,
          unavailableCode: null,
          credentialContext:
            out.credentialContext === undefined
              ? 'CONTEXT_CHECK_FAILED'
              : out.credentialContext,
          lastFetchMs,
        });
        // Only a reply for the live identity may sound: an org switch moves
        // the identity before this hook's next refresh re-arms its scope.
        if (applied && identityNow() === identity) feedPolledFills('polymarket', identity, out.fills.map(polyFillSound));
      } else if (out.code) {
        // #457: a failed read is NOT an empty history. carry() retains the
        // current scope's rows — only a validated success (above) may rewrite
        // them, including to [].
        apply({
          ...carry(),
          status: 'unavailable',
          error: out.error,
          unavailableCode: out.code,
          credentialContext: out.code,
          lastFetchMs,
        });
      } else {
        apply({
          ...carry(),
          status: 'error',
          error: out.error,
          unavailableCode: null,
          lastFetchMs,
        });
      }
    } finally {
      // A late old-scope request must not clear the guard a newer scope owns.
      if (inFlightRef.current === scope) inFlightRef.current = null;
    }
  }, [limit, identity]);

  // Initial fetch + polling lifecycle. `identity` is not listed: it is already
  // a dependency of `refresh`, so an org switch recreates that callback and
  // re-runs this effect — tearing the old timer down and firing an immediate
  // fetch under the new scope.
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    // Failed reads retain the last trustworthy rows. Polling continues below
    // so a credential/org transition can recover without a manual refresh;
    // only a validated success may replace rows, including with an
    // authoritative [].
    const tick = () => {
      if (!stopped) void refresh();
    };
    const intervalId = window.setInterval(tick, pollMs);
    return () => {
      stopped = true;
      window.clearInterval(intervalId);
    };
  }, [enabled, pollMs, refresh]);

  return { ...current, refresh };
}
